// redux/calendar_ICS/saga.js

import { takeLatest, call, put } from 'redux-saga/effects';
import {
  FETCH_ICS_REQUEST,
  FETCH_ICS_SUCCESS,
  FETCH_ICS_FAILURE,
} from './constants';
import { EVENT_CATEGORIES } from '../../constants/utils';
import ICAL from 'ical.js';

// Parse ICS function (you can import this if it's defined elsewhere)
function parseICS(icsText) {
  const jcalData = ICAL.parse(icsText);
  const comp = new ICAL.Component(jcalData);
  const vevents = comp.getAllSubcomponents('vevent');

  const eventCategoryMap = new Map();

  const events = vevents.map((veventComp) => {
    const vevent = new ICAL.Event(veventComp);
    const isAllDay = vevent.startDate.isDate;
    let startDate, endDate;

    if (isAllDay) {
      // For all-day events, vevent.startDate and vevent.endDate are ICAL.Time objects with date-only values
      // Convert to JavaScript Date objects
      startDate = vevent.startDate.toJSDate();
      endDate = vevent.endDate.toJSDate();
      // Subtract one day from endDate to make it inclusive
      endDate.setDate(endDate.getDate() - 1);
      // Set the time to the end of the day (23:59:59) if needed
      endDate.setHours(23, 59, 59, 999);
      //otherwise all day events are from 2024-10-08T00:00:00.00Z to 2024-10-09T00:00:00.00Z  
      //now we fix by making it end on 2024-10-08T23:59:59.999Z
    } else {
      startDate = vevent.startDate.toJSDate();
      endDate = vevent.endDate.toJSDate();
    }
    

     // Default category
     let eventCategory = 'Others';
     let eventCategoryName = 'Others';
     let eventCategoryColor = '#3A5F6B'; // Default color for 'Others'
 

    // Normalize the summary to lower case for case-insensitive matching
    const summary = vevent.summary ? vevent.summary.toLowerCase() : '';

    // Flag to check if a matching category is found
    let categoryFound = false;

    // Loop through EVENT_CATEGORIES to find a match
    for (const category of EVENT_CATEGORIES) {
      if (category.keywords && category.keywords.length > 0) {
        for (const keyword of category.keywords) {
          const regex = new RegExp(`\\b${keyword}\\b`, 'i'); // Word boundary to match whole words
          if (regex.test(summary)) {
            eventCategory = category.id;
            eventCategoryName = category.name;
            eventCategoryColor = category.color;
            categoryFound = true;
            break; // Break the inner loop if a match is found
          }
        }
      }
      if (categoryFound) {
        break; // Break the outer loop if a match is found
      }
    }

    // Add to calendar map if not already added
    if (!eventCategoryMap.has(eventCategory)) {
      eventCategoryMap.set(eventCategory, {
        id: eventCategory,
        category_name: eventCategoryName,
        backgroundColor: eventCategoryColor,
        borderColor: eventCategoryColor,
      });
    }

    return {
      id: vevent.uid || String(Math.random()),
      eventCategory: eventCategory,
      title: vevent.summary || 'No Title',
      category: isAllDay ? 'allday' : 'time',
      start: startDate,
      end: endDate, //isAllDay ? startDate : endDate
      location: vevent.location || '',
      isAllDay: isAllDay,
      backgroundColor: eventCategoryColor,
      color: '#ffffff', // Text color
      borderColor: eventCategoryColor,
      body: vevent.description || '',
    };
  });

  const eventCategories = Array.from(eventCategoryMap.values());

  return { events, eventCategories };
}


function* ics_getter(API_ICS_GETTER, icsUrl) {
    try {
      const response = yield call(fetch, API_ICS_GETTER, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'ICS-URL': icsUrl,
        },
      });
  
      if (!response.ok) {
        const errorText = yield response.text();
        return { status: 'error', message: `API Error: ${response.status} ${errorText}` };
      }
  
      const data = yield call([response, 'json']);
  
      return data; // Return the parsed JSON directly
    } catch (error) {
      console.log('ics_getter Network Error:', error);
      return { status: 'error', message: error.message || 'Network error occurred' };
    }
  }

function* fetchIcsSaga(action) {
  const { icsUrl } = action.payload;
  const API_ICS_GETTER = process.env.REACT_APP_ICS_GETTER_URL;

  try {
    const response = yield call(ics_getter, API_ICS_GETTER, icsUrl);

    if (!response || response.status !== 'success') {
      console.log('API Error Response:', response);

      const errorMessage =
        (response && response.message) ||
        'Unknown error occurred while fetching ICS data';

      yield put({
        type: FETCH_ICS_FAILURE,
        payload: { error: errorMessage },
      });
    } else if (!response.icsData) {
      // Handle the case where icsData is missing
      console.log('API Response Missing icsData:', response);
      yield put({
        type: FETCH_ICS_FAILURE,
        payload: { error: 'Received invalid data from the server' },
      });
    } else {
      const icsText = response.icsData;
      const { events, eventCategories } = parseICS(icsText);
      yield put({ type: FETCH_ICS_SUCCESS, payload: { events, eventCategories } });
    }
  } catch (error) {
    console.log('Saga Catch Error:', error);
    const errorMessage = error && error.message ? error.message : 'An unexpected error occurred';
    yield put({ type: FETCH_ICS_FAILURE, payload: { error: errorMessage } });
  }
}


export default function* calendarSaga() {
  yield takeLatest(FETCH_ICS_REQUEST, fetchIcsSaga);
}
